import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { formatCurrency } from "@/utils/formatters";
import { WrappedCardFrame } from "./WrappedCardFrame";
import type { NextFocusData, WrappedCard } from "../types";

interface NextFocusCardProps {
  card: WrappedCard;
}

export const NextFocusCard = ({ card }: NextFocusCardProps) => {
  const { t } = useTranslation("wrapped");
  const rawData = card.data as Partial<NextFocusData> | null;
  const data =
    rawData && ["budget_overage", "close_gap", "protect_surplus"].includes(rawData.kind ?? "")
      ? (rawData as NextFocusData)
      : null;

  return (
    <WrappedCardFrame title={card.title} body={card.body}>
      {data ? (
        <div className="rounded-[var(--ds-radius-md)] bg-[var(--app-primary-chip-bg)] px-4 py-4 text-sm text-[var(--app-text)]">
          <p className="font-semibold">
            {data.kind === "budget_overage"
              ? t("wrapped.focusReduce", {
                  category: data.category_name,
                  amount: formatCurrency(data.excess ?? 0),
                })
              : data.kind === "close_gap"
                ? t("wrapped.focusCloseGap", { amount: formatCurrency(data.target_amount ?? 0) })
                : t("wrapped.focusProtectSurplus", { amount: formatCurrency(data.target_amount ?? 0) })}
          </p>
          {data.kind === "budget_overage" && data.projected_balance != null ? (
            <p className="mt-2 text-[var(--app-text-secondary)]">
              {t(data.remaining_gap && data.remaining_gap > 0 ? "wrapped.focusStillNegative" : "wrapped.focusPositive", {
                balance: formatCurrency(data.projected_balance),
                gap: formatCurrency(data.remaining_gap ?? 0),
              })}
            </p>
          ) : null}
          {data.kind === "budget_overage" ? (
            <Link
              to="/budget"
              className="mt-4 inline-flex min-h-11 items-center justify-center rounded-xl bg-[var(--app-primary)] px-4 font-semibold text-[var(--app-on-primary)] transition hover:bg-[var(--app-primary-hover)]"
            >
              {t("wrapped.reviewBudgets")}
            </Link>
          ) : null}
        </div>
      ) : null}
    </WrappedCardFrame>
  );
};
